<template>
  <div class="min-h-screen">
    <NavBar :signed-in="signedIn" :user-name="userName" active-page="messages" />
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <h1 class="text-2xl font-bold text-gray-900 mb-6">{{ t('messages.title') }}</h1>

      <!-- Search -->
      <div class="mb-4">
        <input v-model="search" type="text" :placeholder="t('messages.search_placeholder')"
               class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Conversations list -->
      <div v-if="filteredConversations.length" class="space-y-2">
        <a v-for="conv in filteredConversations" :key="conv.id"
           :href="`/conversations/${conv.id}`"
           class="block bg-white rounded-xl shadow-sm border border-gray-200 p-4 hover:bg-gray-50 transition">
          <div class="flex items-center gap-3">
            <span class="inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-sm font-medium text-blue-800 shrink-0">
              {{ conv.other_user?.name?.charAt(0)?.toUpperCase() || '?' }}
            </span>
            <div class="flex-1 min-w-0">
              <div class="flex items-center justify-between">
                <span :class="['text-sm font-medium', conv.unread_count > 0 ? 'text-gray-900' : 'text-gray-700']">
                  {{ conv.other_user?.name || t('messages.unknown_user') }}
                </span>
                <span class="text-xs text-gray-400 shrink-0 ml-2">{{ formatDate(conv.last_message?.created_at || conv.updated_at) }}</span>
              </div>
              <div class="flex items-center justify-between mt-0.5">
                <p class="text-sm text-gray-500 truncate">
                  <span v-if="conv.last_message">
                    <span v-if="conv.last_message.user_id === userId" class="text-gray-400">{{ t('messages.you') }}: </span>
                    {{ conv.last_message.body }}
                  </span>
                  <span v-else class="italic">{{ t('messages.no_messages') }}</span>
                </p>
                <span v-if="conv.unread_count > 0"
                      class="ml-2 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-blue-600 rounded-full shrink-0">
                  {{ conv.unread_count }}
                </span>
              </div>
            </div>
          </div>
        </a>
      </div>

      <p v-else class="text-center text-gray-400 text-sm py-8">{{ t('messages.no_conversations') }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import NavBar from './NavBar.vue'

const props = defineProps({ signedIn: Boolean, userName: String, userId: Number, conversations: Array, csrfToken: String })
const { t } = useI18n()

const search = ref('')

const filteredConversations = computed(() => {
  if (!search.value.trim()) return props.conversations
  const q = search.value.toLowerCase()
  return props.conversations.filter(c =>
    c.other_user?.name?.toLowerCase().includes(q) ||
    c.other_user?.display_name?.toLowerCase().includes(q)
  )
})

function formatDate(d) {
  if (!d) return ''
  const date = new Date(d)
  const now = new Date()
  const diffMs = now - date
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

  if (diffDays === 0) return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
  if (diffDays < 7) return date.toLocaleDateString(undefined, { weekday: 'short' })
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
</script>
